// PRO Drift Auditor -- 5-strategy growth-of-capital comparison with regime
// shading bands behind the AI PRO curve.

import React, { useMemo } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { LineChart as LineChartIcon } from 'lucide-react';

import { Card } from './Card';
import { fmtMoney, fmtMoneyShort } from '../format';
import { REGIME_META, type ProTimeSeriesPoint } from '../types';

// Collapse consecutive equal regime codes into shaded [start,end] bands so the
// chart shades High-Vol-Caution (amber) and Drawdown-Shield (rose) periods.
function regimeBands(data: ProTimeSeriesPoint[]) {
    const bands: { x1: string; x2: string; code: number }[] = [];
    if (data.length === 0) return bands;
    let startIdx = 0;
    for (let i = 1; i <= data.length; i++) {
        const prev = data[i - 1].regime_state_code;
        const curr = i < data.length ? data[i].regime_state_code : null;
        if (curr !== prev) {
            if (prev !== 0) {
                bands.push({ x1: data[startIdx].date, x2: data[i - 1].date, code: prev });
            }
            startIdx = i;
        }
    }
    return bands;
}

export const WealthChart: React.FC<{ data: ProTimeSeriesPoint[] }> = ({ data }) => {
    const bands = useMemo(() => regimeBands(data), [data]);
    return (
        <Card
            title="Growth of Capital"
            subtitle="Cumulative wealth across six disciplines — shaded bands mark Forge Ultra non-bull regimes."
            icon={<LineChartIcon className="h-4 w-4" />}
        >
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 6, right: 10, left: 6, bottom: 0 }}>
                        <defs>
                            <linearGradient id="proWealthFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.4} vertical={false} />
                        {bands.map((b, i) => (
                            <ReferenceArea
                                key={i}
                                x1={b.x1}
                                x2={b.x2}
                                fill={b.code === 2 ? '#f43f5e' : '#f59e0b'}
                                fillOpacity={0.07}
                                ifOverflow="extendDomain"
                            />
                        ))}
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            minTickGap={56}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={fmtMoneyShort}
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            width={56}
                            tickLine={false}
                            axisLine={false}
                            domain={['auto', 'auto']}
                        />
                        <Tooltip
                            formatter={(v: number, name) => [fmtMoney(v), name]}
                            contentStyle={{
                                borderRadius: 12,
                                border: '1px solid #e2e8f0',
                                fontSize: 12,
                                background: 'rgba(255,255,255,0.97)',
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area
                            type="monotone"
                            dataKey="ai_pro_wealth"
                            name="Forge Ultra Regime PRO"
                            stroke="#10b981"
                            strokeWidth={2.75}
                            fill="url(#proWealthFill)"
                        />
                        <Line type="monotone" dataKey="drifted_wealth" name="Unchecked Drift" stroke="#f43f5e" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="calendar_wealth" name="Annual Calendar" stroke="#0ea5e9" strokeWidth={1.75} dot={false} />
                        <Line type="monotone" dataKey="corridor_wealth" name="Static Corridor" stroke="#6366f1" strokeWidth={1.75} dot={false} />
                        <Line
                            type="monotone"
                            dataKey="cashflow_corridor_wealth"
                            name="Cash-Flow Corridor"
                            stroke="#f59e0b"
                            strokeWidth={1.75}
                            strokeDasharray="4 3"
                            dot={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-4 rounded-sm" style={{ background: REGIME_META.HIGH_VOL_CAUTION.color, opacity: 0.4 }} />
                    High-Vol Caution
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-4 rounded-sm" style={{ background: REGIME_META.DRAWDOWN_SHIELD.color, opacity: 0.4 }} />
                    Drawdown Shield
                </span>
            </div>
        </Card>
    );
};
